import {
  ColumnHeightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {Button, Checkbox, Dropdown, Tooltip, Typography} from 'antd';
import type {ColumnsType, ColumnType} from 'antd/es/table';
import React, {RefObject, useEffect, useMemo, useState} from 'react';
import {useTranslation} from '../../hooks/useTranslation';

export type TableDensity = 'large' | 'middle' | 'small';

const getColumnKey = (col: ColumnType<any>): string =>
  String((col as any).key ?? (col as any).dataIndex ?? '');

interface TableToolbarProps {
  columns: ColumnsType<any>;
  visibleColumnKeys: string[];
  onVisibleColumnKeysChange: (keys: string[]) => void;
  size: TableDensity;
  onSizeChange: (size: TableDensity) => void;
  onReload?: () => void;
  isLoading?: boolean;
  fullscreenTarget?: RefObject<HTMLElement | null>;
  showDensity?: boolean;
  showFullscreen?: boolean;
  title?: React.ReactNode;
  extra?: React.ReactNode;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  columns,
  visibleColumnKeys,
  onVisibleColumnKeysChange,
  size,
  onSizeChange,
  onReload,
  isLoading,
  fullscreenTarget,
  showDensity = false,
  showFullscreen = false,
  title,
  extra,
}) => {
  const {t} = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    const target = fullscreenTarget?.current;
    if (!target) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      target.requestFullscreen?.();
    }
  };

  const toggleableColumns = useMemo(
    () => columns.filter((c) => !!getColumnKey(c as ColumnType<any>)),
    [columns]
  );

  const columnSettingsContent = (
    <div
      style={{
        padding: 8,
        minWidth: 180,
        background: 'var(--ant-color-bg-elevated, #fff)',
        boxShadow: 'var(--ant-box-shadow-secondary, 0 6px 16px rgba(0,0,0,0.08))',
        borderRadius: 8,
      }}
    >
      {toggleableColumns.map((col) => {
        const key = getColumnKey(col as ColumnType<any>);
        const checked = visibleColumnKeys.includes(key);
        return (
          <div key={key} style={{padding: '4px 0'}}>
            <Checkbox
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  onVisibleColumnKeysChange([...visibleColumnKeys, key]);
                } else {
                  onVisibleColumnKeysChange(
                    visibleColumnKeys.filter((k) => k !== key)
                  );
                }
              }}
            >
              {String((col as any).title ?? key)}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        gap: 12,
      }}
    >
      <div style={{minWidth: 0}}>
        {title && <Typography.Text strong>{title}</Typography.Text>}
      </div>
      <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
        {extra}
        {onReload && (
          <Tooltip title={t('tableToolbar.reload')}>
            <Button
              type="text"
              icon={<ReloadOutlined spin={isLoading}/>}
              onClick={onReload}
            />
          </Tooltip>
        )}
        {showDensity && (
          <Dropdown
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [size],
              items: [
                {key: 'large', label: t('tableToolbar.densityDefault')},
                {key: 'middle', label: t('tableToolbar.densityMiddle')},
                {key: 'small', label: t('tableToolbar.densityCompact')},
              ],
              onClick: ({key}) => onSizeChange(key as TableDensity),
            }}
          >
            <Tooltip title={t('tableToolbar.density')}>
              <Button type="text" icon={<ColumnHeightOutlined/>}/>
            </Tooltip>
          </Dropdown>
        )}
        <Dropdown
          trigger={['click']}
          popupRender={() => columnSettingsContent}
        >
          <Tooltip title={t('tableToolbar.columns')}>
            <Button type="text" icon={<SettingOutlined/>}/>
          </Tooltip>
        </Dropdown>
        {showFullscreen && fullscreenTarget && (
          <Tooltip
            title={
              isFullscreen
                ? t('tableToolbar.exitFullscreen')
                : t('tableToolbar.fullscreen')
            }
          >
            <Button
              type="text"
              icon={
                isFullscreen ? <FullscreenExitOutlined/> : <FullscreenOutlined/>
              }
              onClick={toggleFullscreen}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export const useTableToolbar = (columns: ColumnsType<any>, initialSize: TableDensity = 'small') => {
  const allKeys = useMemo(
    () =>
      columns
        .map((c) => getColumnKey(c as ColumnType<any>))
        .filter((k): k is string => !!k),
    [columns]
  );
  const allKeysSignature = allKeys.join('|');

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(allKeys);
  const [size, setSize] = useState<TableDensity>(initialSize);

  useEffect(() => {
    setVisibleColumnKeys((prev) => {
      const known = new Set(allKeys);
      const kept = prev.filter((k) => known.has(k));
      const added = allKeys.filter((k) => !prev.includes(k));
      return [...kept, ...added];
    });
  }, [allKeysSignature]);

  const visibleColumns = useMemo(
    () =>
      columns.filter((c) => {
        const key = getColumnKey(c as ColumnType<any>);
        if (!key) return true;
        return visibleColumnKeys.includes(key);
      }),
    [columns, visibleColumnKeys]
  );

  return {
    size,
    setSize,
    visibleColumnKeys,
    setVisibleColumnKeys,
    visibleColumns,
  };
};

export default TableToolbar;
